import React, { useState, useEffect } from "react";
import axios from "axios";
import classes from "./Department.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiRefreshCw,
} from "react-icons/fi";
import AddDepartmentModal from "./AddDepartmentModal";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENT ---
const SkeletonRow = () => (
  <tr className={classes.skeletonRow}>
    <td><div className={classes.skeletonCell}></div></td>
    <td><div className={classes.skeletonCell}></div></td>
    <td><div className={classes.skeletonCell}></div></td>
    <td><div className={classes.skeletonCell}></div></td>
    <td><div className={classes.skeletonCell}></div></td>
  </tr>
);

const Department = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Data State
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");

  // Fetch departments from API
  const fetchDepartments = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${BASE_URL}/api/department`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      console.log("Departments API Response:", response.data);

      if (response.data && response.data.data) {
        let departmentsData = response.data.data;
        
        // Handle different response structures
        if (departmentsData.departments) {
          departmentsData = departmentsData.departments;
        }
        
        // If it's a single object, wrap in array
        if (Array.isArray(departmentsData)) {
          setDepartments(departmentsData);
        } else {
          setDepartments([departmentsData]);
        }
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      let errorMsg = "Failed to load departments. Please try again.";
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Load departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Handle department added successfully
  const handleDepartmentAdded = (newDepartment) => {
    fetchDepartments();
  };

  // Filter departments based on search term and type
  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch = dept.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          dept.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          dept.type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType ? dept.type === filterType : true;
    
    return matchesSearch && matchesType;
  });

  // Get unique department types for filter
  const departmentTypes = [...new Set(departments.map(dept => dept.type).filter(Boolean))];

  // Handle delete department
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/department/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      fetchDepartments();
    } catch (error) {
      console.error("Error deleting department:", error);
      alert("Failed to delete department. Please try again.");
    }
  };

  // Render skeleton loaders
  const renderSkeletons = () => {
    return Array(5).fill(0).map((_, index) => (
      <SkeletonRow key={`skeleton-${index}`} />
    ));
  };

  return (
    <div className={classes.pageContainer}>
      {/* Add Department Modal */}
      <AddDepartmentModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)}
        onDepartmentAdded={handleDepartmentAdded}
      />

      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={classes.mainArea}>
        <Header toggleSidebar={toggleSidebar} />

        <div className={classes.content}>
          {/* Top Header */}
          <div className={classes.topHeader}>
            <div>
              <h1 className={classes.pageTitle}>Department</h1>
              <p className={classes.pageSubtitle}>Manage all hospital departments</p>
            </div>
            <button className={classes.refreshBtn} onClick={fetchDepartments} disabled={loading}>
              <FiRefreshCw className={loading ? classes.spinning : ""} />
              Refresh
            </button>
          </div>

          {/* Toolbar */}
          <div className={classes.toolbar}>
            <div className={classes.searchWrapper}>
              <input
                type="text"
                placeholder="Search departments..."
                className={classes.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
              <FiSearch className={classes.searchIcon} />
            </div>
            <div className={classes.toolbarRight}>
              {departmentTypes.length > 0 && (
                <select 
                  className={classes.filterSelect}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All Types</option>
                  {departmentTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              )}
              
              <button className={classes.addBtn} onClick={() => setIsAddModalOpen(true)} disabled={loading}>
                <FiPlus /> Add Department
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={classes.errorMessage}>
              <span>{error}</span>
              <button onClick={fetchDepartments} className={classes.retryBtn}>
                Retry
              </button>
            </div>
          )}

          {/* Table with Skeleton Loader */}
          <div className={classes.tableCard}>
            {loading ? (
              // Skeleton Loading State
              <div className={classes.skeletonWrapper}>
                <table className={classes.dataTable}>
                  <thead>
                    <tr>
                      <th>Department Name</th>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderSkeletons()}
                  </tbody>
                </table>
              </div>
            ) : filteredDepartments.length === 0 ? (
              <div className={classes.emptyState}>
                <p>No departments found</p>
                {searchTerm && <p>Try adjusting your search</p>}
                {!searchTerm && <p>Click "Add Department" to create one</p>}
              </div>
            ) : (
              <table className={classes.dataTable}>
                <thead>
                  <tr>
                    <th>Department Name</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDepartments.map((dept) => (
                    <tr key={dept.id}>
                      <td className={classes.nameCell}>{dept.name || "N/A"}</td>
                      <td className={classes.codeCell}>{dept.code || "N/A"}</td>
                      <td className={classes.typeCell}>{dept.type || "N/A"}</td>
                      <td>
                        <span className={classes.statusBadge}>
                          {dept.status || "Active"}
                        </span>
                      </td>
                      <td>
                        <div className={classes.actions}>
                          <button className={classes.actionBtn} title="View">
                            <FiEye />
                          </button>
                          <button className={classes.actionBtn} title="Edit">
                            <FiEdit2 />
                          </button>
                          <button 
                            className={`${classes.actionBtn} ${classes.deleteBtn}`}
                            onClick={() => handleDelete(dept.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Department Count */}
          {!loading && !error && (
            <div className={classes.tableFooter}>
              <span>Total Departments: {filteredDepartments.length}</span>
              {searchTerm && <span> (Filtered from {departments.length})</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Department;